import { T } from "@angular/cdk/keycodes";
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild } from "@angular/core";
import { FormControl } from "@angular/forms";
import { MatAutocomplete, MatAutocompleteTrigger } from "@angular/material/autocomplete";
import { VesselService } from "app/shared/services/vessel.service";
import { forEach } from "lodash";
import { Observable, Subject, of } from "rxjs";
import { bufferTime, concatMap, debounceTime, delay, distinctUntilChanged, finalize, map, startWith, switchMap } from "rxjs/operators";

/**
 * @title Multi-select autocomplete
 */
@Component({
  selector: "multiselect-autocomplete",
  templateUrl: "multiselect-autocomplete.component.html",
  //styleUrls: ["multiselect-autocomplete.component.scss"]
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class MultiselectAutocompleteComponent implements OnInit
, OnChanges 
{
  @ViewChild(MatAutocomplete) auto: MatAutocomplete;
  @ViewChild(MatAutocompleteTrigger) autocompleteTrigger: MatAutocompleteTrigger;

  @ViewChild("dataInput") private _dataInput: ElementRef;
  inputControl = new FormControl();

  @Input() datas = [];
  @Input() placeholder: string;
  @Input() endpoint: string;
  @Input() keyValue: string;
  @Input() disable: boolean;
  @Output() selectChange = new EventEmitter<any>();

  selectedDatas: any[] = new Array<any>();
  filteredOptions: Observable<any[]>;

  filteredDatas: any[];
  lastFilter: string = "";

  private toggleSubject = new Subject<any>();

  constructor(private _vesselService: VesselService, private cdr: ChangeDetectorRef) {
      
  }

  setFilter(values: any[]) {
    let filter = []; 
    
    values.forEach(element => {
      let o = {};
      o[this.keyValue] = element;
      o['selected'] = true;

      filter.push(o);
    });
    
    if (filter.length == 0) {
      this.selectedDatas = [];
      this.inputControl.setValue('');
      this.cdr.detectChanges();

      // this.filteredOptions = this._vesselService.getDistinctName('');

    } else {
      this.selectedDatas = filter;
      this.inputControl.setValue('');
      this.cdr.detectChanges();
    }

  }

  ngOnInit() {
    
    if (!this.endpoint) {

       // Handle grouped toggling with buffering and additional delay
        this.toggleSubject.pipe(
          bufferTime(6000), // Buffer all events in the last 3 seconds
          switchMap(dataArray => 
            of(dataArray).pipe(
            )
          )
        ).subscribe(dataArray => {
          if (dataArray.length > 0) {
            this.handleToggle(dataArray);
          }
        });

      this.inputControl.valueChanges.subscribe(value => {
        this.lastFilter = typeof value === "string" ? value : this.lastFilter;
        this.filteredDatas = this.filter(this.lastFilter);
        
        this.selectedDatas.forEach(x => {
          this.filteredDatas.push(x);
        });

        this.filteredDatas = this.getDistinctObjects(this.filteredDatas, 'value')


        this.filteredDatas = this.filteredDatas.sort((a, b) => {
          if (a.selected && !b.selected) {
            return -1; // 'a' comes before 'b' if 'a' is selected and 'b' is not
          } else if (!a.selected && b.selected) {
            return 1; // 'b' comes before 'a' if 'b' is selected and 'a' is not
          } else {
            return 0; // Maintain the original order if both or neither are selected
          }
        });
        this.cdr.detectChanges();
      });
    } else {
      this.filteredOptions = this.inputControl.valueChanges.pipe(
        debounceTime(500),
        distinctUntilChanged(),
        switchMap((value) => {
          if (value && value.length >= 3) {
            return this._vesselService.getDistinctName(value.toUpperCase()).pipe(
              map((data) => {
                // Modify data or add items here before returning
                let modifiedData = [...data, ...this.selectedDatas];
                modifiedData = modifiedData.sort((a, b) => {
                  if (a.selected && !b.selected) {
                    return -1; // 'a' comes before 'b' if 'a' is selected and 'b' is not
                  } else if (!a.selected && b.selected) {
                    return 1; // 'b' comes before 'a' if 'b' is selected and 'a' is not
                  } else {
                    return 0; // Maintain the original order if both or neither are selected
                  }
                });
                return modifiedData;
              }),
              finalize(() => {this.cdr.detectChanges();})
            );
          } else {
            // Return an observable with the additional item when the condition is not met
            return of([]);
          }
        })
      );
    }
    
  }

  getDistinctObjects(array: any[], property: keyof any): any[] {
 
    const uniqueValues = new Set();
    return array.filter(obj => {
      const value = obj[property];
      if (!uniqueValues.has(value)) {
        uniqueValues.add(value);
        return true;
      }
      return false;
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    const valueChange = changes['datas'];
    const disableBind = changes['disable'];

    if (disableBind) {
      if (disableBind.currentValue)
        this.inputControl.disable();
      else 
        this.inputControl.enable();
    }
    if (valueChange) {
      this.filteredDatas = valueChange.currentValue;
      if (valueChange.currentValue.length == 0)
        this.selectedDatas = [];
    }
  }

  filter(filter: string): any[] {
    this.lastFilter = filter;
    if (filter) {
      return this.datas.filter(option => {
        return (
          option.value.toLowerCase().indexOf(filter.toLowerCase()) >= 0
        );
      });
    } else {
      return this.datas.slice();
    }
  }

  displayFn(value: any[] | string): string | undefined {
    let displayValue: string;
    if (Array.isArray(value)) {
      value.forEach((data, index) => {
        if (index === 0) {
          displayValue = data.value;
        } else {
          displayValue += ", " + data.value;
        }
      });
    } else {
      displayValue = value;
    }
    return displayValue;
  }

  displayFnV2(value: any[] | string): string | undefined {
    let displayValue: string;
    if (Array.isArray(value)) {
      value.forEach((data, index) => {
        if (index === 0) {
          displayValue = data[this.keyValue];
        } else {
          displayValue += ", " + data[this.keyValue];
        }
      });
    } else {
      displayValue = value;
    }
    return displayValue;
  }

  onClear(event) {
    event.stopPropagation();
    event.preventDefault();
    this.inputControl.setValue("");
    this._dataInput.nativeElement.focus();
    this.autocompleteTrigger.closePanel();
    if (!this.endpoint) {
      this.selectedDatas.filter(s => s.selected).forEach(item => {
        this.toggleSelection(item, true);
      });
    } else {
      // this.selectedDatas.filter(s => s.selected).forEach(item => {
      //   this.toggleSelectionV2(item);
      // });
      this.selectedDatas = [];
      this.selectChange.emit(this.selectedDatas);

    }
    }
    

  optionClicked(event: Event, data: any) {
    event.stopPropagation();
    this.toggleSelection(data);
  
    this.autocompleteTrigger.closePanel();
  }

  optionClickedV2(event: Event, data: any) {
    event.stopPropagation();
    this.toggleSelectionV2(data);
    this.autocompleteTrigger.closePanel();
  }

  toggleSelection(data: any, isClear: boolean = false) {
    if (!isClear)
      // Emit the data to the subject
      this.toggleSubject.next(data);
    else {
      data.selected = !data.selected;
      const i = this.selectedDatas.findIndex(
        value =>
          value.value === data.value
      );
      this.selectedDatas.splice(i, 1);

      this.inputControl.setValue("");
      this._dataInput.nativeElement.focus();
      this.autocompleteTrigger.closePanel();

      this.selectChange.emit(this.selectedDatas);
    }

  }

  handleToggle(data: any) {
    data.forEach(element => {
      element.selected = !element.selected;
      if (element.selected) {
        this.selectedDatas.push(element);
      } else {
        const i = this.selectedDatas.findIndex(
          value =>
            value.value === element.value
        );
        this.selectedDatas.splice(i, 1);
      }
    });

    // this.userControl.setValue(this.selectedUsers);
    this.inputControl.setValue("");
    this._dataInput.nativeElement.focus();
    this.autocompleteTrigger.closePanel();

    this.selectChange.emit(this.selectedDatas);
  }

  toggleSelectionV2(data: any) {
    data.selected = !data.selected;
    if (data.selected) {
      this.selectedDatas.push(data);
    } else {
      const i = this.selectedDatas.findIndex(
        value =>
          value[this.keyValue] === data[this.keyValue]
      );
      this.selectedDatas.splice(i, 1);
    }

    // this.userControl.setValue(this.selectedUsers);
    // this.inputControl.setValue("");
    this._dataInput.nativeElement.focus();
    this.autocompleteTrigger.closePanel();
    this.selectChange.emit(this.selectedDatas);
  }
}
